import React, { useState } from "react";
import {
  alpha,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import PlaylistAddCheckCircleOutlinedIcon from "@mui/icons-material/PlaylistAddCheckCircleOutlined";

function ActionProgressRow({ action, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        minHeight: 70,
        px: 2,
        display: "grid",
        gridTemplateColumns: "28px minmax(0, 1fr) auto 20px",
        gap: 1.5,
        alignItems: "center",
        cursor: "pointer",
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:hover": {
          bgcolor: alpha("#17345d", 0.025),
        },
      }}
    >
      {action.complete ? (
        <CheckCircleRoundedIcon sx={{ color: "success.main", fontSize: 21 }} />
      ) : (
        <RadioButtonUncheckedRoundedIcon sx={{ color: "text.disabled", fontSize: 21 }} />
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 15.5, fontWeight: 600, color: "text.primary" }}>
          {action.title}
        </Typography>
        {action.setupText ? (
          <Typography sx={{ mt: 0.25, fontSize: 12.5, color: "text.secondary" }}>
            {action.setupText}
          </Typography>
        ) : null}
        {Array.isArray(action.usedIn) && action.usedIn.length > 0 ? (
          <Stack direction="row" spacing={0.7} flexWrap="wrap" sx={{ mt: 0.7 }}>
            {action.usedIn.map((label) => (
              <Chip key={label} size="small" label={label} sx={{ height: 22, fontSize: 11.5, fontWeight: 700 }} />
            ))}
          </Stack>
        ) : null}
        {action.smallVersionTitle ? (
          <Box
            sx={{
              mt: 0.8,
              px: 1.15,
              py: 0.8,
              borderRadius: 2,
              border: "1px solid",
              borderColor: alpha("#2d8a5f", 0.18),
              bgcolor: alpha("#2d8a5f", 0.08),
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                lineHeight: 1.2,
                fontWeight: 800,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                color: "success.dark",
              }}
            >
              Keep-alive step
            </Typography>
            <Typography sx={{ mt: 0.25, fontSize: 13.5, fontWeight: 700, color: "text.primary" }}>
              {action.smallVersionTitle}
            </Typography>
            {action.intelligenceNote ? (
              <Typography sx={{ mt: 0.25, fontSize: 12.5, color: "text.secondary" }}>
                {action.intelligenceNote}
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </Box>
      <Typography
        sx={{
          fontSize: 13.5,
          fontWeight: 700,
          color: action.complete ? "success.dark" : "primary.main",
          whiteSpace: "nowrap",
        }}
      >
        {action.progressLabel}
      </Typography>
      <ChevronRightRoundedIcon sx={{ color: "text.disabled" }} />
    </Box>
  );
}

export default function WeeklyActionsCard({
  actions,
  hasGoal,
  goalTitle,
  onAddAction,
  onEditAction,
}) {
  const [showAll, setShowAll] = useState(false);
  const visibleActions = showAll ? actions : actions.slice(0, 6);
  const hiddenCount = Math.max(actions.length - visibleActions.length, 0);

  return (
    <Paper elevation={0} sx={{ borderRadius: "8px", p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.25 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha("#17345d", 0.08),
              color: "primary.main",
            }}
          >
            <PlaylistAddCheckCircleOutlinedIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {goalTitle ? `Habits for ${goalTitle}` : "Your habits"}
            </Typography>
            <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
              {goalTitle
                ? "Edit cadence, planned time, and keep-alive steps."
                : "Edit timing, cadence, and keep-alive steps."}
            </Typography>
          </Box>
        </Box>
        <Button size="small" startIcon={<AddIcon />} onClick={onAddAction} disabled={!hasGoal}>
          Add habit
        </Button>
      </Box>

      {!hasGoal ? (
        <Typography sx={{ color: "text.secondary" }}>
          Add a goal first, then choose the habits that support it this week.
        </Typography>
      ) : actions.length ? (
        <Box sx={{ borderRadius: "8px", overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
          {visibleActions.map((action) => (
            <ActionProgressRow
              key={action.id}
              action={action}
              onClick={() => onEditAction(action.commitment)}
            />
          ))}
          {hiddenCount > 0 || (showAll && actions.length > 6) ? (
            <Button
              fullWidth
              onClick={() => setShowAll((value) => !value)}
              sx={{
                py: 1.15,
                textTransform: "none",
                fontWeight: 800,
                color: "text.secondary",
                bgcolor: "#f8fafc",
                borderRadius: 0,
              }}
            >
              {showAll ? "Show fewer habits" : `Show ${hiddenCount} more`}
            </Button>
          ) : null}
        </Box>
      ) : (
        <Typography sx={{ color: "text.secondary" }}>
          Add the small repeatable habits that make this goal real.
        </Typography>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
        <Button size="small" startIcon={<AddIcon />} onClick={onAddAction} disabled={!hasGoal}>
          Add habit
        </Button>
      </Stack>
    </Paper>
  );
}
